import type { Polygon, MultiPolygon } from 'geojson';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': import.meta.env.VITE_API_KEY || 'dev-key-12345',
  },
});

/**
 * Submarket boundary with GeoJSON geometry and properties.
 */
export interface SubmarketBoundary {
  submarket_id: string;
  name: string;
  geometry: Polygon | MultiPolygon;
  properties: {
    cagr_5yr: number;
    rank: number;
    egi: number;
  };
}

/**
 * Raw GeoJSON response from the API.
 */
export interface SubmarketGeoJSONResponse {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: {
      submarket_id: string | number;
      name: string;
      cagr_5yr: number;
      rank: number;
      egi: number;
    };
    geometry: Polygon | MultiPolygon;
  }>;
}

/**
 * Hook to fetch submarket boundary geometries.
 *
 * Transforms GeoJSON FeatureCollection into SubmarketBoundary array
 * for use with Google Maps polygon rendering.
 */
export function useSubmarketBoundaries() {
  return useQuery<SubmarketBoundary[]>({
    queryKey: ['submarkets', 'geojson'],
    queryFn: async () => {
      const { data } = await api.get<SubmarketGeoJSONResponse>('/submarkets/geojson');

      // Transform features to SubmarketBoundary format
      return data.features.map((feature) => ({
        submarket_id: String(feature.properties.submarket_id),
        name: feature.properties.name,
        geometry: feature.geometry,
        properties: {
          cagr_5yr: feature.properties.cagr_5yr,
          rank: feature.properties.rank,
          egi: feature.properties.egi,
        },
      }));
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - boundary data rarely changes
    refetchOnWindowFocus: false,
  });
}

/**
 * Result for a single property in a batch underwriting run.
 */
export interface BatchPropertyResult {
  property_id: string;
  address?: string;
  neighborhood?: string;
  tier?: string;
  total_units?: number;
  purchase_price?: number;
  noi?: number;
  cap_rate?: number;
  cash_on_cash?: number;
  irr_5yr?: number;
  irr_10yr?: number;
  equity_multiple?: number;
  dscr?: number;
  ltv?: number;
  verdict: string;
  verdict_reasons?: string[];
  status?: string;
  error_message?: string;
}

export default api;
